import { Controller } from '@hotwired/stimulus';

interface CroppieOptions {
  viewport: { width: number; height: number; type: string };
  boundary: { width: number; height: number };
  enforceBoundary: boolean;
}

interface CroppieInstance {
  element: Element;
  elements: { img: HTMLImageElement; viewport: HTMLElement };
  bind(options: { url: string }): Promise<void>;
  get(): { points: string[] };
  setZoom(value: number): void;
  destroy(): void;
}

// Loaded via the page-scoped :picture Sprockets bundle, not bundled with webpack (SOFT-2).
declare global {
  interface Window {
    Croppie?: new (element: Element, options: CroppieOptions) => CroppieInstance;
  }
}

interface Geometry {
  x: number;
  y: number;
  w: number;
  h: number;
}

const PAD = 10;

// D-030-12: the slider is centred on contain (whole logo visible, padding around it), so the
// range is symmetric about it: 0.1x to 1.9x. Centring contain forces ceiling < 2*contain, so for
// a logo much wider than the window the zoom cannot reach "fills the window" -- accepted trade.
const ZOOM_SPREAD = 0.9;
// Croppie's own precedent for the zoom ceiling (croppie.js:852-854): never below 1.5, and
// give 1 unit of headroom above "fills the viewport" once that already exceeds 1.5.

export default class extends Controller<HTMLElement> {
  static targets = ['stage', 'cropX', 'cropY', 'cropW', 'cropH'];

  static values = {
    viewportWidth: Number,
    viewportHeight: Number,
    viewportType: String,
    inputName: String
  };

  declare readonly stageTarget: HTMLElement;
  declare readonly cropXTarget: HTMLInputElement;
  declare readonly cropYTarget: HTMLInputElement;
  declare readonly cropWTarget: HTMLInputElement;
  declare readonly cropHTarget: HTMLInputElement;

  declare readonly viewportWidthValue: number;
  declare readonly viewportHeightValue: number;
  declare readonly viewportTypeValue: string;
  declare readonly inputNameValue: string;

  private form: HTMLFormElement | null = null;
  private croppie: CroppieInstance | null = null;

  private readonly onChange = (event: Event) => this.handleChange(event);
  private readonly onCleared = (event: Event) => this.handleCleared(event as CustomEvent<{ input: HTMLInputElement }>);
  private readonly onSubmit = () => this.handleSubmit();

  connect(): void {
    const form = this.element.closest('form');

    this.form = form;
    form?.addEventListener('change', this.onChange);
    form?.addEventListener('file-input:cleared', this.onCleared);
    form?.addEventListener('submit', this.onSubmit);

    this.stageTarget.style.width = `${this.viewportWidthValue + 2 * PAD}px`;
  }

  disconnect(): void {
    this.form?.removeEventListener('change', this.onChange);
    this.form?.removeEventListener('file-input:cleared', this.onCleared);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.teardown();
  }

  private handleChange(event: Event): void {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || !target.matches(`input[type="file"][name="${this.inputNameValue}"]`)) {
      return;
    }

    const file = target.files?.[0];

    if (!file) {
      this.teardown();
      return;
    }

    this.mount(file);
  }

  private handleCleared(event: CustomEvent<{ input: HTMLInputElement }>): void {
    if (event.detail.input.name !== this.inputNameValue) {
      return;
    }

    this.teardown();
  }

  private handleSubmit(): void {
    const file = this.fileInput()?.files?.[0];

    if (!file) {
      this.teardown();
    }
  }

  private fileInput(): HTMLInputElement | null {
    return this.form?.querySelector<HTMLInputElement>(`input[type="file"][name="${this.inputNameValue}"]`) ?? null;
  }

  private mount(file: File): void {
    const CroppieCtor = window.Croppie;

    if (!CroppieCtor) {
      return;
    }

    const reader = new FileReader();

    reader.onload = (loadEvent: ProgressEvent<FileReader>) => {
      const url = loadEvent.target?.result as string;

      this.teardown();
      this.stageTarget.hidden = false;

      const width = Math.min(this.viewportWidthValue, this.stageTarget.clientWidth - 2 * PAD);
      const height = width * (this.viewportHeightValue / this.viewportWidthValue);

      this.croppie = new CroppieCtor(this.stageTarget, {
        viewport: { width, height, type: this.viewportTypeValue },
        boundary: { width: width + 2 * PAD, height: height + 2 * PAD },
        // D-030-12: zooming out below "fills the viewport" requires this — minZoom and the
        // pan/zoom clamps are only applied behind this flag (croppie.js:846-850, :503-526, :611-628).
        enforceBoundary: false
      });

      this.croppie.element.addEventListener('update', () => this.writePoints());

      // The slider's min/max only exist once Croppie has measured the loaded image inside
      // bind(), so the initial-zoom offset is applied off its promise rather than inline here.
      this.croppie.bind({ url }).then(() => this.applyInitialZoom());
    };

    reader.readAsDataURL(file);
  }

  private applyInitialZoom(): void {
    if (!this.croppie) {
      return;
    }

    const slider = this.stageTarget.querySelector<HTMLInputElement>('.cr-slider');

    if (!slider) {
      return;
    }

    const { naturalWidth, naturalHeight } = this.croppie.elements.img;
    const viewportRect = this.croppie.elements.viewport.getBoundingClientRect();

    const contain = Math.min(viewportRect.width / naturalWidth, viewportRect.height / naturalHeight);
    const cover = Math.max(viewportRect.width / naturalWidth, viewportRect.height / naturalHeight);

    if (!Number.isFinite(contain) || !Number.isFinite(cover)) {
      return;
    }

    const floor = contain * (1 - ZOOM_SPREAD);
    const ceiling = contain * (1 + ZOOM_SPREAD);

    if (floor <= 0 || ceiling <= floor) {
      return;
    }

    slider.min = floor.toFixed(4);
    slider.max = ceiling.toFixed(4);

    this.croppie.setZoom(contain);
  }

  private writePoints(): void {
    if (!this.croppie) {
      return;
    }

    const [x1, y1, x2, y2] = this.croppie.get().points.map((value) => parseInt(value, 10));

    this.write({ x: x1, y: y1, w: x2 - x1, h: y2 - y1 });
  }

  private write(geometry: Geometry | null): void {
    this.cropXTarget.value = geometry ? String(geometry.x) : '';
    this.cropYTarget.value = geometry ? String(geometry.y) : '';
    this.cropWTarget.value = geometry ? String(geometry.w) : '';
    this.cropHTarget.value = geometry ? String(geometry.h) : '';
  }

  private teardown(): void {
    this.croppie?.destroy();
    this.croppie = null;
    this.stageTarget.hidden = true;
    this.write(null);
  }
}
